import { Field, Formik } from 'formik';
import React, { useContext, useState, useEffect } from 'react';
import { Environment } from 'src/Environment';

import TealiumEvents from 'src/helpers/tealium-events';
import { DownArrow1 as DownArrow1Icon, UpArrow as UpArrowIcon } from 'src/assets/images';
import { Card, LoaderAnimation } from 'src/components';
import {
  SalesforceContext,
  AcquiringChannelFlags,
  CustomerTypes,
  LockedFieldNames,
  ecommerceProductCategoryName,
  paymentRequestProductCategoryName,
} from 'src/context/salesforce-context';
import { FeesContext } from 'src/context/fees-context';
import { checkAcquiringChannel } from 'src/helpers/devices';
import { formatCurrencyAmount, formatCurrencyAmountSimple } from 'src/helpers/formatting';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import SpinEditReskin from 'src/components/spin-edit-reskin/spin-edit-reskin';
import DeviceQuantityChangeConflictResolver from '../device-quantity-reduce-confirm-modal/device-quantity-change-conflict-resolver';
import { vatPercent } from 'src/helpers/constants';
import { ReducerContext } from 'src/context/reducer-context';
import { WizardMessageNames } from '../../state/wizard/wizard-message-names';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';

import CreditVettingDetailsModal from '../../credit-vetting-incomplete-modal/credit-vetting-incomplete-modal';
import {
  PRODUCT_IMAGE_MAP,
  DEVICE_NAMES,
} from 'src/features/order/tech-debt/product-details-extra';
import './choose-solution.scss';

const BIZ_WALLET_PRO_DEVICE_NAME = 'Biz Wallet Pro';
export default function ChooseSolution(props) {
  const deviceQuantitySelectedLimit = 70;
  const {
    lead,
    uniqueTerminals,
    merchant,
    setDeviceOrder,
    getDeviceOrderQuantity,
    getDeviceDeliveryQuantity,
    solutionType,
    customerType,
    setIsIncompleteSOVJourney,
    isUseFixedCardFeesForDisplay,
    isFieldLocked,
    isLoadingFromSessionStorage,
    isLeadConvertedAsync,
    setAllDeliveriesInCategoryToZero,
    isProductCategory,
  } = useContext(SalesforceContext);
  const { cardFees, terminalFees, isAllFeesLoaded, getFilteredCardFees } = useContext(FeesContext);
  const [isInitialJourneyWithoutMax, setIsInitialJourneyWithoutMax] = useState(false);
  const [creditVettingJourneyIncomplete, setCreditVettingJourneyIncomplete] = useState(false);
  const [showCreditVettingDetailsModal, setShowCreditVettingDetailsModal] = useState(false);
  const [categoryRemoved, setCategoryRemoved] = useState('');
  const { dispatch, registerReducer } = useContext(ReducerContext);
  const [feesLoaded, setFeesLoaded] = useState(false);
  const [isBankingDetailsIncomplete] = useState(false);

  const isRented = checkAcquiringChannel(
    AcquiringChannelFlags.rentOnlineFicaOnline,
    merchant?.opportunity?.deliveries,
  );

  const isCreditVettingEnabled = !Environment.current?.disabledFeatures?.creditVetting && isRented;
  const [isDevIncentive, setIsDevIncentive] = useState(false);

  const [showMore, setShowMore] = useState(
    Object.values(uniqueTerminals).reduce(
      (map, item) =>
        (map[item.id] = {
          showMore: false,
        }),
      {},
    ),
  );

  const [deviceQuantities, setDeviceQuantities] = useState(getDeviceQuantities());
  useEffect(() => {
    props.toggleNext(false);
    setIsInitialJourneyWithoutMax(!isCreditVettingEnabled && props.isQuickNavigateState);
  }, []);

  useEffect(() => {
    if (lead.salesOrg) {
      setIsDevIncentive(true);
    }
  }, []);

  useEffect(() => {
    if (isInitialJourneyWithoutMax) {
      setCreditVettingJourneyIncomplete(isCreditVettingEnabled);
    }
  }, [merchant]);

  useEffect(() => {
    if (cardFees?.cardFeesData?.length > 0) {
      setFeesLoaded(true);
    }
  }, [cardFees]);

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const productList = [];
      Object.values(uniqueTerminals)
        .filter((term) => solutionType.includes(term.acquiring_channel))
        .forEach((product) => {
          productList.push(product.name);
        });

      TealiumEvents.chooseYourSolutionPageLoad(
        merchant.account.business_type,
        solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline) ||
          customerType === CustomerTypes.pos
          ? 'in-store'
          : 'online',
        customerType,
        productList,
      );
    }
  }, [merchant?.account?.business_type]);
  useEffect(() => setDeviceQuantities(getDeviceQuantities()), [merchant]);

  useEffect(() => {
    if (!!merchant.opportunity && isDevIncentive) {
      setDeviceQuantities(setDefaultTerminal(DEVICE_NAMES.VODAPAY_ECOM_GATEWAY));
      setDeviceOrder(
        getTerminalIDFromTerminalName(DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
        1,
        getDeviceFee(getTerminalIDFromTerminalName(DEVICE_NAMES.VODAPAY_ECOM_GATEWAY)),
      );
    }
  }, [merchant?.opportunity]);

  useEffect(() => {
    if (!!categoryRemoved) {
      setAllDeliveriesInCategoryToZero(categoryRemoved);
    }
  }, [categoryRemoved]);

  const getConsumerBasedText = (consumerType) =>
    consumerType === CONSUMER_TYPE.owner
      ? {
          title: 'Choose your solution, you can select multiple.',
          titleBuyInstore: 'Add your devices to your profile',
          subtitle: 'Select the solution/s best suited to your needs.',
          subtitleBuyInstore: 'Let us know how many devices you purchased',
        }
      : {
          title: 'Choose a solution, you can select multiple.',
          titleBuyInstore: 'Add your devices to the customers profile.',
          subtitle: "Select the solution/s best suited to the customer's needs",
          subtitleBuyInstore: 'Let us know how many devices the customer purchased',
        };

  const textTable = {
    dealer: {
      title: 'Choose a solution, you can select multiple.',
      titleBuyInstore: 'Add your devices to your customers profile.',
      subtitle: 'Select the solution/s best suited to your customer needs',
      subtitleBuyInstore: 'Let us know how many devices your customer purchased',
    },
    agent: {
      title: 'Choose a solution, you can select multiple.',
      titleBuyInstore: 'Choose a solution, you can select multiple.',
      subtitle: 'Select the solution/s best suited to your customer needs.',
      subtitleBuyInstore: 'Select the solution/s best suited to your customer needs.',
    },
    normal: { ...getConsumerBasedText(lead.consumerType) },
    pos: {
      title: 'VodaPay Kwika fees',
      titleBuyInstore: 'VodaPay Kwika fees',
      subtitle: 'Fees are based on your estimated monthly card turnover',
      subtitleBuyInstore: 'Fees are based on your estimated monthly card turnover',
    },
  };

  const bizWalletProInfo = [
    { name: 'Inflow per month', detail: 'Unlimited' },
    { name: 'Outflow per month', detail: 'Unlimited' },
    { name: 'Wallet limit', detail: 'R 100 000' },
    { name: 'Daily cash withdrawal limit', detail: 'R 20 000' },
    { name: 'Daily cash send', detail: 'Unlimited' },
  ];

  const getCardFees = (terminalId) => {
    const terminal = uniqueTerminals[terminalId];
    const isCardPresentStatus = terminal.card_present ? 'Card Present' : 'Card Not Present';
    const cardFees = getFilteredCardFees(isUseFixedCardFeesForDisplay(terminalId));

    const applicableCardFees = (cardFees || []).filter(
      (cardFee) => cardFee.feeCategoryName === isCardPresentStatus,
    );
    return applicableCardFees.map((cardFee) => ({
      name: cardFee.cardTypeBouquetName,
      amount: `${cardFee.percentage}%`,
    }));
  };

  const getDeviceFee = (terminalId) => {
    const fee = terminalFees?.TerminalFeesData?.find(
      (fee) => fee.deviceModelName === uniqueTerminals[terminalId].name,
    );
    return fee ? fee.fixedFee : null;
  };

  const toggleShowMore = (id, terminalName, isMobile) => (state) => {
    let newState = { ...state };
    if (isMobile) {
      for (var key in newState) {
        if (key !== id) {
          newState[key] = !newState[id];
        }
      }
    }
    newState[id] = !newState[id];

    if (newState[id]) {
      const onlineOrInstore = solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline)
        ? 'in-store'
        : 'online';
      TealiumEvents.chooseYourSolutionShowMore(terminalName, customerType, onlineOrInstore);
    }
    return newState;
  };

  const onDeviceQuantityChange = (terminalId, numberOfDevices, isManualEdit) => {
    const deviceNewQuantity = parseInt(numberOfDevices);
    const deviceExistingQuantity = getDeviceOrderQuantity(terminalId);
    const deviceDetails = uniqueTerminals[terminalId];
    const deviceFee =
      deviceDetails.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline
        ? deviceDetails.retail_fee_vat_excl
        : getDeviceFee(terminalId);

    setDeviceOrder(terminalId, deviceNewQuantity, deviceFee);
    if (numberOfDevices === 0 && isProductCategory(terminalId)) {
      setCategoryRemoved(deviceDetails.product_category);
    }

    // SOV journey, this logic needs to be relooked at. Check kwika credit vetting logic
    if (isCreditVettingEnabled && props.isQuickNavigateState) {
      const isIncompleteSOVJourneyNewValue =
        deviceDetails.acquiring_channel === AcquiringChannelFlags.vodapay &&
        deviceExistingQuantity < 1 &&
        numberOfDevices > 0;
      setIsIncompleteSOVJourney(isIncompleteSOVJourneyNewValue);
    }

    if (deviceExistingQuantity !== deviceNewQuantity) {
      const onlineOrInstore = solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline)
        ? 'in-store'
        : 'online';
      TealiumEvents.chooseYourSolutionProductsQuantityChange(
        deviceDetails.name,
        deviceFee,
        deviceExistingQuantity,
        numberOfDevices,
        isManualEdit,
        customerType,
        onlineOrInstore,
      );
    }

    const totalDevicesCurrent = getTotalDevices(merchant);

    if (
      totalDevicesCurrent > deviceQuantitySelectedLimit &&
      deviceExistingQuantity !== deviceNewQuantity
    ) {
      toastMessageServiceSingleton.addError(
        'Maximum 70 terminals in total, please adjust your quantity.',
        null,
        'N/A',
        true,
      );
    }
  };

  useEffect(() => {
    if (isCreditVettingEnabled) {
      const unregister = registerReducer({
        apply: (message) => {
          switch (message.name) {
            case WizardMessageNames.NAVIGATE_TO_NEXT_STEP:
              if (creditVettingJourneyIncomplete) {
                setShowCreditVettingDetailsModal(true);
                setCreditVettingJourneyIncomplete(false);
                props.unsetQuickNavigate();
                return {
                  discardMessage: true,
                };
              }
              break;
          }
          return {};
        },
        applicableMessageNames: [WizardMessageNames.NAVIGATE_TO_NEXT_STEP],
      });

      return () => {
        unregister();
      };
    }
  }, [isBankingDetailsIncomplete, creditVettingJourneyIncomplete]);

  useEffect(() => {
    let newShowMore = {};
    for (var key in uniqueTerminals) {
      let terminalID = uniqueTerminals[key].id;
      newShowMore[terminalID] = showMore[terminalID];
    }
    setShowMore(newShowMore);
  }, [uniqueTerminals]);

  const getTotalDevices = (merchant) =>
    Object.values(merchant?.opportunity?.deliveries || {})
      .map((delivery) => delivery.number_of_devices)
      .reduce((a, b) => a + b, 0);

  const refreshNextButtonState = (merchant, isLoadingFromSessionStorage, feesLoaded) => {
    if (!isLoadingFromSessionStorage && feesLoaded) {
      const totalDevicesCurrent = getTotalDevices(merchant);
      props.toggleNext(
        totalDevicesCurrent > 0 && totalDevicesCurrent <= deviceQuantitySelectedLimit,
      );
    }
  };

  useEffect(
    () => refreshNextButtonState(merchant, isLoadingFromSessionStorage, feesLoaded),
    [merchant, isLoadingFromSessionStorage, feesLoaded],
  );

  useEffect(
    () => refreshNextButtonState(merchant, isLoadingFromSessionStorage, feesLoaded),
    [isLoadingFromSessionStorage, feesLoaded],
  );

  function quantityFieldNameToDeviceId(fieldname) {
    return fieldname.substring(16);
  }

  function deviceIdToQuantityFieldName(deviceId) {
    return `Quantity_Device_${deviceId}`;
  }

  function onQuantityChange(values, isManualEdit) {
    Object.keys(values).forEach((key) => {
      const deviceId = quantityFieldNameToDeviceId(key);
      const deviceQuantity = values[key];
      const minimumDeviceQuantity = getDeviceDeliveryQuantity(deviceId);
      if (deviceQuantity < minimumDeviceQuantity) {
        setDeviceIdToUpdate(deviceId);
        setDeviceQuantityToUpdate(deviceQuantity);
        setDeviceQuantityIsManualUpdate(isManualEdit);
      } else {
        onDeviceQuantityChange(deviceId, deviceQuantity, isManualEdit);
      }
    });

    return {};
  }

  function getDeviceQuantities() {
    return Object.values(uniqueTerminals).reduce((accum, terminal) => {
      const deliveryInfo = merchant?.opportunity?.deliveries?.[terminal.id];
      const numberOfDevices = deliveryInfo ? deliveryInfo.number_of_devices : 0;
      const fieldName = deviceIdToQuantityFieldName(terminal.id);
      accum[fieldName] = numberOfDevices;
      return accum;
    }, {});
  }

  function getTerminalIDFromTerminalName(terminalName) {
    return Object.keys(uniqueTerminals)
      .filter((key) => uniqueTerminals[key].name === terminalName)
      .reduce((accum, terminal) => (accum[terminalName] = terminal), {});
  }

  function setDefaultTerminal(terminal) {
    const terminals = getDeviceQuantities();
    Object.keys(terminals).forEach(
      (key) =>
        (terminals[key] = Number(
          key === deviceIdToQuantityFieldName(getTerminalIDFromTerminalName(terminal)),
        )),
    );
    return terminals;
  }

  const [deviceQuantityToUpdate, setDeviceQuantityToUpdate] = useState(null);
  const [deviceIdToUpdate, setDeviceIdToUpdate] = useState(null);
  const [deviceQuantityIsManualUpdate, setDeviceQuantityIsManualUpdate] = useState(null);

  const getHeadingText = (solutionType, isMobile = false) => {
    if (
      solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline) ||
      solutionType.includes(AcquiringChannelFlags.vodacomBusiness)
    ) {
      return isMobile
        ? textTable[customerType].subtitleBuyInstore
        : textTable[customerType].titleBuyInstore;
    } else {
      return isMobile
        ? 'Choose your device that fits your business need'
        : textTable[customerType].title;
    }
  };

  const getSubHeadingText = (solutionType) => {
    if (solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline)) {
      return textTable[customerType].subtitleBuyInstore;
    } else {
      return textTable[customerType].subtitle;
    }
  };

  const filterSOVForMultiDirectorCompanies = (terminal) => {
    if (merchant?.account?.id_numbers?.length > 1) {
      return terminal.acquiring_channel !== AcquiringChannelFlags.vodapay;
    } else {
      return true;
    }
  };

  const showEcommerce = (term) => {
    if (
      term.product_category !== ecommerceProductCategoryName &&
      term.product_category !== paymentRequestProductCategoryName
    ) {
      return true;
    }
    return !term.product_category || (!!term.product_category && term.is_product_category);
  };

  const getFilteredTerminals = () => {
    let filteredTerminals = Object.values(uniqueTerminals)
      .filter((term) => {
        return solutionType.includes(term.acquiring_channel) && showEcommerce(term);
      })
      .filter(filterSOVForMultiDirectorCompanies);
    return [
      ...filteredTerminals.filter((product) => product.name === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
      ...filteredTerminals.filter((product) => product.name !== DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
    ];
  };

  const isQuantityFieldDisabled = isFieldLocked(
    LockedFieldNames.OPPORTUNITY_DELIVERIES_NUMBER_OF_DEVICES,
  );

  const isDeviceHasPrice = (terminal) =>
    [
      AcquiringChannelFlags.buyOnlineFicaOnline,
      AcquiringChannelFlags.rentOnlineFicaOnline,
      AcquiringChannelFlags.zeroRentOnlineFicaOnline,
      AcquiringChannelFlags.vodapay,
    ].includes(terminal.acquiring_channel) &&
    ((getDeviceFee(terminal.id) && getDeviceFee(terminal.id) !== 0) ||
      (uniqueTerminals[terminal.id].retail_fee_vat_excl &&
        terminal.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline));

  const getDevicePrice = (terminal) =>
    terminal.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline
      ? uniqueTerminals[terminal.id].retail_fee_vat_excl
      : getDeviceFee(terminal.id);

  const getFeeDisplay = (terminal) => {
    if (terminal.acquiring_channel !== AcquiringChannelFlags.buyInstoreFicaOnline) {
      return isDeviceHasPrice(terminal) ? (
        <div className="frequency-text">
          <div className="price">R {formatCurrencyAmountSimple(getDevicePrice(terminal))}</div>

          <div className="permonth">
            /{' '}
            {terminal.acquiring_channel === AcquiringChannelFlags.rentOnlineFicaOnline ||
            terminal.acquiring_channel === AcquiringChannelFlags.zeroRentOnlineFicaOnline
              ? 'month'
              : 'once off'}
          </div>
        </div>
      ) : (
        <div className="frequency-text">
          <div className="price">Free</div>
        </div>
      );
    } else {
      return (
        <div className="frequency-text">
          <div className="price">Pay in store</div>
        </div>
      );
    }
  };

  return (
    <>
      <div className="container ChooseSolution">
        <CreditVettingDetailsModal
          show={showCreditVettingDetailsModal}
          onButtonClick={() => {
            setShowCreditVettingDetailsModal(false);
            dispatch(WizardMessageNames.NAVIGATE_TO_NEXT_STEP);
          }}
        />
        <DeviceQuantityChangeConflictResolver
          deviceId={deviceIdToUpdate}
          deviceQuantity={deviceQuantityToUpdate}
          deviceQuantityChangeAcceptCallback={(deviceId, deviceQuantity) => {
            onDeviceQuantityChange(deviceId, deviceQuantity, deviceQuantityIsManualUpdate);
            setDeviceQuantityToUpdate(null);
            setDeviceIdToUpdate(null);
            setDeviceQuantityIsManualUpdate(null);
          }}
          deviceQuantityChangeRejectCallback={() => {
            setDeviceQuantityToUpdate(null);
            setDeviceIdToUpdate(null);
            setDeviceQuantityIsManualUpdate(null);
          }}
        />

        <div className="heading-area">
          <div className="heading-four main-heading is-hidden-mobile has-padding-bottom-3">
            <p>{getHeadingText(solutionType)}</p>
          </div>

          <div className="heading-four main-heading has-padding-x-1 is-hidden-tablet">
            <p>{getHeadingText(solutionType, true)}</p>
          </div>

          <div className="sub-heading content-normal is-hidden-mobile">
            <p>{getSubHeadingText(solutionType)}</p>
          </div>
        </div>

        <div className="container">
          <div className="columns is-mobile scrolling-wrapper">
            <div className="column" />

            <Formik initialValues={deviceQuantities} enableReinitialize={true}>
              {({ values: terminalQuantities }) => {
                {
                  return getFilteredTerminals().map((terminal) => (
                    <div
                      className="pb-2 column is-narrow card-padding"
                      key={terminal.id}
                      automationid={`Onboarding_Create_Account_Choose_Solution_Div_Terminal_${terminal.id}`}
                    >
                      <div className="card-image has-text-centered">
                        <figure className="image is-inline-block device-image">
                          <img src={PRODUCT_IMAGE_MAP[terminal.name]} />
                        </figure>
                      </div>
                      <Card>
                        <div className="card-padding">
                          <header className="card-header">
                            <p className="card-header-title is-centered content-normal box-title has-padding-bottom-5">
                              {terminal.name}
                            </p>
                          </header>
                          {terminal.name === DEVICE_NAMES.VODAPAY_MAX && (
                            <p className="sub-text has-padding-x-6">
                              One device, multiple functions, seamless payments.
                            </p>
                          )}
                          {terminal.name === BIZ_WALLET_PRO_DEVICE_NAME && (
                            <p className="sub-text has-padding-x-6">
                              Enjoy the full potential of your wallet with unlimited daily
                              transactions.
                            </p>
                          )}
                          {terminal.name === DEVICE_NAMES.VODAPAY_CHOP_CHOP && (
                            <p className="sub-text">
                              Accepts all Masterpass wallets (e.g. VodaPay).
                            </p>
                          )}
                          {terminal.name === DEVICE_NAMES.VODAPAY_KWIKA && (
                            <p className="sub-text">
                              One device, multiple functions, seamless payments.
                            </p>
                          )}
                          {terminal.name === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY && (
                            <p className="sub-text">
                              Offer your customers a variety of payment options – debit/credit card,
                              EFT and QR payments
                            </p>
                          )}
                          {terminal.name === DEVICE_NAMES.TAP_ON_PHONE && (
                            <p className="sub-text">
                              Accept payments using your Android smartphone.
                            </p>
                          )}

                          {getFeeDisplay(terminal)}
                          <div className="vat content-small-regular">
                            {isDeviceHasPrice(terminal) ? (
                              <span>
                                + R {formatCurrencyAmount(getDevicePrice(terminal) * vatPercent)}{' '}
                                VAT
                              </span>
                            ) : (
                              <span className="vat-not-applicable" />
                            )}
                          </div>
                          <div className="bar" />

                          <div>
                            <h3 className="fees-per-transaction-heading heading-six has-padding-bottom-6 has-mobile-padding-bottom-4">
                              {terminal.name === BIZ_WALLET_PRO_DEVICE_NAME
                                ? 'Transaction limits'
                                : 'Fees per transaction*'}
                            </h3>

                            {terminal.name !== BIZ_WALLET_PRO_DEVICE_NAME && (
                              <h3 className="fees-per-transaction-subtitle content-small-regular">
                                *Excluding VAT
                              </h3>
                            )}

                            <div className="div-table fees-per-transaction fee-text">
                              {isAllFeesLoaded() &&
                                isLeadConvertedAsync &&
                                terminal.name !== BIZ_WALLET_PRO_DEVICE_NAME &&
                                getCardFees(terminal.id).map((fee, index) => (
                                  <div key={index} className="columns is-mobile">
                                    <div className="column is-1 is-narrow">-</div>
                                    <div className="column is-narrow is-8 content-normal ">
                                      {fee.name}
                                    </div>
                                    <div className="column is-narrow is-3 content-normal">
                                      {fee.amount}
                                    </div>
                                  </div>
                                ))}

                              {isAllFeesLoaded() &&
                                terminal.is_product_category &&
                                terminal.product_category === ecommerceProductCategoryName && (
                                  <div key={'ozow'} className="columns is-mobile">
                                    <div className="column is-1 is-narrow">-</div>
                                    <div className="column is-narrow is-8 content-normal">Ozow</div>
                                    <div className="column is-narrow is-3 content-normal">1.5%</div>
                                  </div>
                                )}

                              {isAllFeesLoaded() &&
                                isLeadConvertedAsync &&
                                terminal.name === BIZ_WALLET_PRO_DEVICE_NAME &&
                                bizWalletProInfo.map((info, index) => (
                                  <div key={index} className="columns is-mobile">
                                    <div className="column is-1 is-narrow">-</div>
                                    <div className="column is-narrow is-6 content-normal">
                                      {info.name}
                                    </div>
                                    <div className="column is-narrow is-4 has-text-right content-normal">
                                      {info.detail}
                                    </div>
                                    <div className="column is-narrow is-1 content-normal"></div>
                                  </div>
                                ))}

                              {(!isAllFeesLoaded() || !isLeadConvertedAsync) && (
                                <div className="has-text-centered has-padding-left-6">
                                  <LoaderAnimation height={200} width={200} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {showMore[terminal.id] && terminal.description && (
                          <div
                            className="container div-table"
                            automationIdPrefix={`Onboarding_Div_Extra_Information_${terminal.id}`}
                          >
                            <div className="additional-features heading-six">
                              {terminal.name === BIZ_WALLET_PRO_DEVICE_NAME
                                ? 'Wallet features'
                                : 'Additional features'}
                            </div>

                            <div className="extra-info">
                              {terminal.description.split(/\r?\n/).map((detail) => (
                                <div key={detail} className="columns is-gapless is-mobile">
                                  <div className="column is-1 is-narrow">-</div>
                                  <div className="column ">{detail}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="card-padding">
                          {terminal.description && (
                            <div className="show-more-center">
                              <span
                                className="show-more is-hidden-mobile"
                                onClick={() =>
                                  setShowMore(toggleShowMore(terminal.id, terminal.name, false))
                                }
                                automationid={`Onboarding_Span_Button_Show_More_Toggle_${terminal.id}`}
                              >
                                Show {showMore[terminal.id] ? 'less' : 'more'}
                                {''}
                                <img src={showMore[terminal.id] ? UpArrowIcon : DownArrow1Icon} />
                              </span>
                              <span
                                className="show-more is-hidden-tablet-only is-hidden-desktop-only is-hidden-widescreen-only is-hidden-fullhd"
                                onClick={() =>
                                  setShowMore(toggleShowMore(terminal.id, terminal.name, true))
                                }
                                automationid={`Onboarding_Span_Button_Show_More_Toggle_${terminal.id}`}
                              >
                                Show {showMore[terminal.id] ? 'less' : 'more'}
                                {''}
                                <img src={showMore[terminal.id] ? UpArrowIcon : DownArrow1Icon} />
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="bottom-shadow" />

                        {/*SECTION*/}
                        <div className="select-section">
                          <div className={'card-padding'}>
                            {feesLoaded && isLeadConvertedAsync && (
                              <Field
                                name={deviceIdToQuantityFieldName(terminal.id)}
                                automationid={`Onboarding_SpinEdit_Quantity_${terminal.id}`}
                                component={SpinEditReskin}
                                min={0}
                                max={
                                  terminal.name === BIZ_WALLET_PRO_DEVICE_NAME ||
                                  terminal.name === DEVICE_NAMES.TAP_ON_PHONE ||
                                  !!terminal.is_product_category
                                    ? 1
                                    : deviceQuantitySelectedLimit
                                }
                                disabled={isQuantityFieldDisabled}
                                onModified={(newValue, isManualEdit) => {
                                  onQuantityChange(
                                    {
                                      ...terminalQuantities,
                                      [deviceIdToQuantityFieldName(terminal.id)]: newValue,
                                    },
                                    isManualEdit,
                                  );
                                }}
                                className="device-quantity-input"
                              />
                            )}
                          </div>
                        </div>
                      </Card>
                    </div>
                  ));
                }
              }}
            </Formik>
            <div className="column" />
          </div>
        </div>
      </div>
    </>
  );
}
